import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  arrayUnion
} from 'firebase/firestore';
import { db, auth } from './firebase';
import type { Wallet, Transaction } from '@festpix/types';

export async function getWallet(walletId: string): Promise<Wallet | null> {
  const walletDoc = await getDoc(doc(db, 'wallets', walletId));

  if (!walletDoc.exists()) {
    return null;
  }

  return {
    id: walletDoc.id,
    ...walletDoc.data()
  } as Wallet;
}

export async function getWalletTransactions(walletId: string): Promise<Transaction[]> {
  // Query transactions where this wallet is the source (purchases, transfers out)
  const fromQuery = query(
    collection(db, 'transactions'),
    where('fromWalletId', '==', walletId),
    orderBy('timestamp', 'desc')
  );

  // Query transactions where this wallet is the destination (recharges, transfers in)
  const toQuery = query(
    collection(db, 'transactions'),
    where('toWalletId', '==', walletId),
    orderBy('timestamp', 'desc')
  );

  const [fromSnapshot, toSnapshot] = await Promise.all([
    getDocs(fromQuery),
    getDocs(toQuery)
  ]);

  const transactionsMap = new Map<string, Transaction>();

  fromSnapshot.forEach((doc) => {
    transactionsMap.set(doc.id, {
      id: doc.id,
      ...doc.data()
    } as Transaction);
  });

  toSnapshot.forEach((doc) => {
    if (!transactionsMap.has(doc.id)) {
      transactionsMap.set(doc.id, {
        id: doc.id,
        ...doc.data()
      } as Transaction);
    }
  });

  // Convert to array and sort by timestamp descending
  const transactions = Array.from(transactionsMap.values());
  transactions.sort((a, b) => {
    const dateA = 'toDate' in a.timestamp ? a.timestamp.toDate() : new Date(a.timestamp);
    const dateB = 'toDate' in b.timestamp ? b.timestamp.toDate() : new Date(b.timestamp);
    return dateB.getTime() - dateA.getTime();
  });

  return transactions;
}

export async function getUserWallet(userId: string): Promise<Wallet | null> {
  const userDoc = await getDoc(doc(db, 'users', userId));

  if (!userDoc.exists()) {
    return null;
  }

  const userData = userDoc.data();
  if (!userData?.walletId) {
    return null;
  }

  return getWallet(userData.walletId);
}

export async function getChildWallets(parentWalletId: string, ownerId: string): Promise<Wallet[]> {
  const q = query(
    collection(db, 'wallets'),
    where('parentWalletId', '==', parentWalletId),
    where('ownerId', '==', ownerId)
  );

  const querySnapshot = await getDocs(q);
  const childWallets: Wallet[] = [];

  querySnapshot.forEach((doc) => {
    childWallets.push({
      id: doc.id,
      ...doc.data()
    } as Wallet);
  });

  return childWallets;
}

export async function createWalletForUser(
  userId: string,
  email: string,
  name: string
): Promise<Wallet> {
  const walletRef = doc(collection(db, 'wallets'));
  const walletId = walletRef.id;

  const qrCodeData = JSON.stringify({ walletId });

  await setDoc(walletRef, {
    balance: 0,
    ownerId: userId,
    qrCode: qrCodeData,
    name: name,
    email: email,
    associatedWallets: [],
    createdAt: new Date(),
  });

  await updateDoc(doc(db, 'users', userId), {
    walletId: walletId,
  });

  return {
    id: walletId,
    balance: 0,
    ownerId: userId,
    qrCode: qrCodeData,
  };
}

export async function createChildWallet(
  parentWalletId: string,
  childName: string
): Promise<Wallet> {
  const parentWalletRef = doc(db, 'wallets', parentWalletId);
  const parentWalletDoc = await getDoc(parentWalletRef);

  if (!parentWalletDoc.exists()) {
    throw new Error('Carteira pai não encontrada');
  }

  const parentData = parentWalletDoc.data();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Usuário não autenticado');
  }

  const childWalletRef = doc(collection(db, 'wallets'));
  const childWalletId = childWalletRef.id;

  const qrCodeData = JSON.stringify({ walletId: childWalletId });

  await setDoc(childWalletRef, {
    balance: 0,
    ownerId: parentData?.ownerId || user.uid,
    qrCode: qrCodeData,
    parentWalletId: parentWalletId,
    childName: childName,
    isChildWallet: true,
    associatedWallets: [],
    createdAt: new Date(),
  });

  await updateDoc(parentWalletRef, {
    associatedWallets: arrayUnion(childWalletId),
  });

  return {
    id: childWalletId,
    balance: 0,
    ownerId: parentData?.ownerId || user.uid,
    qrCode: qrCodeData,
    parentWalletId: parentWalletId,
    childName: childName,
    isChildWallet: true,
  };
}

export type { Wallet, Transaction };
